/**
 * Compose / reply / forward (prototype `openCompose` + `renderCompose`).
 *
 * Recipients are chips: typing an address and pressing Enter or comma commits it, so a
 * half-typed address can't silently become a recipient. Attachments upload immediately and
 * are sent by id (contract §11), which is also why the drop zone can accept a file before
 * the message is finished.
 *
 * The body is a plain textarea rather than the prototype's contenteditable + `execCommand`
 * toolbar: `execCommand` is deprecated and unreliable across browsers, and a real rich-text
 * editor is a dependency decision rather than something to improvise here. The text is
 * wrapped into `body_html` on send, so the wire format still matches the contract.
 *
 * ---- Attachments: A4, not available yet ----
 *
 * A4 (docs/bosun-required-api-round-2.md) — no live endpoint stores an uploaded file and hands
 * back a reference `mail.send_email` can carry, so both ways of adding a file here (the "Attach"
 * button and the drag-and-drop zone) are disabled — see `lib/api/liveAvailability.ts`. A message
 * with no attachment still sends.
 */

import { useState, type DragEvent, type KeyboardEvent } from 'react'
import { Button } from '../../components/ui'
import { Badge } from '../../components/Badge'
import { ATTACHMENT_ICON, toBodyHtml } from '../../lib/mail'
import { useUploadFile } from '../../lib/api/useMail'
import { isLiveFeature, NOT_AVAILABLE_YET } from '../../lib/api/liveAvailability'
import type { ComposePayload, MailAccount, MailAttachment, UploadedFile } from '../../lib/types'

export type ComposeMode = 'new' | 'reply' | 'replyall' | 'forward' | 'draft'

export interface ComposeSeed {
  mode: ComposeMode
  account: MailAccount
  to?: string[]
  cc?: string[]
  subject?: string
  body?: string
  /** Carried over when forwarding, or restored when re-opening a draft. */
  attachments?: MailAttachment[]
  /** The message being replied to, forwarded, or the draft being edited. */
  sourceId?: string
}

const TITLES: Record<ComposeMode, string> = {
  new: 'New message',
  reply: 'Reply',
  replyall: 'Reply all',
  forward: 'Forward',
  draft: 'Edit draft',
}


export function ComposeModal({
  seed,
  sending,
  onSend,
  onClose,
}: {
  seed: ComposeSeed
  sending: boolean
  // No `source`/reply-thread id any more — `mail.send_email` (Task 6) takes no such parameter.
  onSend: (payload: ComposePayload) => void
  onClose: () => void
}) {
  const [to, setTo] = useState<string[]>(seed.to ?? [])
  const [cc, setCc] = useState<string[]>(seed.cc ?? [])
  const [showCc, setShowCc] = useState((seed.cc?.length ?? 0) > 0)
  const [toDraft, setToDraft] = useState('')
  const [ccDraft, setCcDraft] = useState('')
  const [subject, setSubject] = useState(seed.subject ?? '')
  const [body, setBody] = useState(seed.body ?? '')
  const [attachments, setAttachments] = useState<UploadedFile[]>(
    // A forwarded message's files, or a draft's — already stored, so they carry by id.
    (seed.attachments ?? []).map((a) => ({ id: a.id, name: a.name, kind: a.kind, size: a.size })),
  )
  const [dragging, setDragging] = useState(false)

  const upload = useUploadFile()
  const attachAvailable = isLiveFeature('mailAttachmentUpload')

  const commitRecipient = (
    e: KeyboardEvent<HTMLInputElement>,
    draft: string,
    setDraft: (v: string) => void,
    list: string[],
    setList: (v: string[]) => void,
  ) => {
    if (e.key !== 'Enter' && e.key !== ',') return
    e.preventDefault()
    const value = draft.trim().replace(/,$/, '')
    if (value && !list.includes(value)) setList([...list, value])
    setDraft('')
  }

  const addFiles = (files: FileList | null) => {
    if (!attachAvailable || !files?.length) return
    for (const file of Array.from(files)) {
      upload.mutate(file, {
        onSuccess: (uploaded) => setAttachments((current) => [...current, uploaded]),
      })
    }
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    addFiles(e.dataTransfer.files)
  }

  const payload = (): ComposePayload => ({
    account: seed.account,
    // An address typed but not yet committed still counts — losing it on send is worse.
    to: toDraft.trim() ? [...to, toDraft.trim()] : to,
    cc: ccDraft.trim() ? [...cc, ccDraft.trim()] : cc,
    subject,
    body_html: toBodyHtml(body),
    attachments: attachments.map((a) => a.id),
  })

  const recipientCount = payload().to.length
  const isReplyOrForward = seed.mode === 'reply' || seed.mode === 'replyall' || seed.mode === 'forward'

  const chips = (list: string[], remove: (i: number) => void) =>
    list.map((address, i) => (
      <span className="chip" key={address}>
        {address}
        <b onClick={() => remove(i)} title={`Remove ${address}`}>
          ✕
        </b>
      </span>
    ))

  return (
    // No backdrop-close: a stray click outside must not discard a half-written message.
    <div className="modal" role="presentation">
      <div className="box compose" role="dialog" aria-modal="true">
        <div className="chead">
          <span>✉️ {TITLES[seed.mode]}</span>
          <span style={{ cursor: 'pointer' }} onClick={onClose}>
            ✕
          </span>
        </div>

        <div
          className={`cbody cdrop${dragging ? ' drag' : ''}`}
          onDragOver={(e) => {
            e.preventDefault()
            // No drag highlight while attachments are unavailable — an inviting drop zone that
            // then silently drops the file is exactly the "looks live, does nothing" case to
            // avoid.
            if (attachAvailable) setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <div className="crow">
            <label htmlFor="compose-to">To</label>
            {chips(to, (i) => setTo(to.filter((_, x) => x !== i)))}
            <input
              id="compose-to"
              value={toDraft}
              placeholder="Recipients"
              onChange={(e) => setToDraft(e.target.value)}
              onKeyDown={(e) => commitRecipient(e, toDraft, setToDraft, to, setTo)}
            />
            <button type="button" className="cclink" onClick={() => setShowCc((v) => !v)}>
              Cc
            </button>
          </div>

          {showCc ? (
            <div className="crow">
              <label htmlFor="compose-cc">Cc</label>
              {chips(cc, (i) => setCc(cc.filter((_, x) => x !== i)))}
              <input
                id="compose-cc"
                value={ccDraft}
                placeholder="Cc"
                onChange={(e) => setCcDraft(e.target.value)}
                onKeyDown={(e) => commitRecipient(e, ccDraft, setCcDraft, cc, setCc)}
              />
            </div>
          ) : null}

          <div className="crow">
            <label htmlFor="compose-subject">Subject</label>
            <input
              id="compose-subject"
              value={subject}
              // On a reply or forward the server derives the subject from the original, so an
              // editable field here would silently discard what the user typed.
              readOnly={isReplyOrForward}
              title={isReplyOrForward ? 'The subject follows the message being answered' : undefined}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <textarea
            className="ceditor"
            value={body}
            placeholder={attachAvailable ? 'Write your message… (drag files here to attach)' : 'Write your message…'}
            onChange={(e) => setBody(e.target.value)}
          />

          <div className="cattachlist">
            {attachments.map((file, i) => (
              <span className="cchip" key={file.id}>
                <span style={{ fontSize: 16 }}>{ATTACHMENT_ICON[file.kind]}</span>
                <span>{file.name}</span>
                <span style={{ color: 'var(--muted)' }}>{file.size}</span>
                <button
                  type="button"
                  className="x"
                  onClick={() => setAttachments(attachments.filter((_, x) => x !== i))}
                  aria-label={`Remove ${file.name}`}
                >
                  ✕
                </button>
              </span>
            ))}
            {upload.isPending ? <Badge tone="muted">uploading…</Badge> : null}
            {upload.error ? <Badge tone="danger">upload failed: {upload.error.message}</Badge> : null}
          </div>
        </div>

        <div className="cfoot">
          <Button
            variant="primary"
            disabled={sending || recipientCount === 0}
            title={recipientCount === 0 ? 'Add at least one recipient' : undefined}
            onClick={() => onSend(payload())}
          >
            {sending ? 'Sending…' : '➤ Send'}
          </Button>
          {/*
            "Save draft" is gone — `mail.send_email` (Task 6) is the only send-side write; there
            is no "save without sending" method any more (see `useMail.ts`'s module header,
            "Dropped"). A real Drafts folder still exists to READ from (whatever the account's own
            mail client has saved), just not one Waves can write to.
          */}
          <label
            className="btn"
            style={{ cursor: attachAvailable ? 'pointer' : 'not-allowed', opacity: attachAvailable ? 1 : 0.55 }}
            title={attachAvailable ? undefined : NOT_AVAILABLE_YET}
          >
            📎 Attach
            <input type="file" multiple hidden disabled={!attachAvailable} onChange={(e) => addFiles(e.target.files)} />
          </label>
          {attachAvailable ? null : (
            <span style={{ fontSize: 10.5, color: 'var(--muted)' }}>{NOT_AVAILABLE_YET}</span>
          )}
          <span style={{ flex: 1 }} />
          <Button variant="danger" onClick={onClose}>
            Discard
          </Button>
        </div>
      </div>
    </div>
  )
}
